import { useState } from 'react';
import { Color, Size, t } from '@bms2/shared';
import { Button, TextInput } from '../../components/controls';
import { post } from '../records/api';
import { FloorOverrideWindow, type OpenFloor } from './FloorOverrideWindow';
import { approvalActionPath } from './PendingApprovalsPage';
import { refreshMenuCounts } from '../../shell/menuCounts';
import styles from './PendingApprovalsPage.module.css';

/**
 * 🔒 Approve and Reject on the document's own page (decision 0504, extended by
 * decision 0599 to the Quote, the Sales Order and the Work Order): a person
 * reading the document is where the decision is taken, and the Bill, the
 * Purchase Order and the Change Order already worked this way.
 *
 * 🔨 Whether this viewer may act is the document page's own `canApprove`, which
 * the server resolves with the queue's own function (T644; decision 1521): two
 * resolvers for *may this person approve* is how a control appears for somebody
 * the route will refuse. 💰 The control read the whole queue to answer it, on
 * every document an approver opened.
 * 🔨 Reject asks for its reason before it sends anything, the way the queue does.
 *
 * 🔒 Approve on a document under a floor no reason covers asks the approver for
 * the override in a window, and approves in one act (T1195; decision 1861): the
 * window shows each breach sentence and takes one reason, and its confirm writes
 * that reason against every open floor and then the approval. The floors are the
 * page's own reads, so the window costs no request; the server's refusal stays
 * as the backstop. The window is the queue's too (`FloorOverrideWindow`).
 */
export type { OpenFloor };

export interface ApprovalDecisionProps {
  documentType: string;
  documentId: string;
  /** The page answer's own `canApprove`; absent or false draws nothing. */
  canApprove: boolean | undefined;
  apiBaseUrl?: string;
  /** Told after a decision lands, with the sentence for the page's own banner. */
  onDone: (message: string) => void;
  onError: (message: string) => void;
  /** The floors no reason covers yet; Approve opens the override window while any stands (decision 1861). */
  openFloors?: readonly OpenFloor[];
  /** Where a floor's reason is written, required beside `openFloors`. */
  floorAcknowledgementPath?: string;
}

export function ApprovalDecision({ documentType, documentId, canApprove, apiBaseUrl = '', onDone, onError, openFloors = [], floorAcknowledgementPath }: ApprovalDecisionProps) {
  const [reason, setReason] = useState<string | null>(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const asksOverride = openFloors.length > 0 && floorAcknowledgementPath !== undefined;
  const mayAct = canApprove === true && documentId !== '';

  const act = async (action: 'approve' | 'reject', body: Record<string, unknown>, done: string) => {
    setBusy(true);
    const answer = await post<{ fields?: { afterApproval?: string | null } }>(approvalActionPath(documentType, documentId, action), body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return onError(answer.message);
    setReason(null);
    // 🔒 Decision 3072: the decision leaves the menu's Approvals balloon at once, on every document page this control sits on.
    refreshMenuCounts();
    // 🔒 Approve sends a Draft in the same press (T223; decision 0713), and the approver reads what happened to the send.
    onDone(answer.data?.fields?.afterApproval ?? done);
  };

  if (!mayAct) return null;
  return (
    <>
      <span data-approval-action="approve" data-approval-asks-override={asksOverride ? 'true' : 'false'}>
        <Button size={Size.Medium} color={Color.Navy} text={[t('approvals.approve')]} disabled={busy} actions={[() => (asksOverride ? setOverride(true) : void act('approve', {}, t('approvals.approved')))]} />
      </span>
      <Button size={Size.Medium} color={Color.Inactive} text={[t('approvals.reject')]} disabled={busy} actions={[() => setReason('')]} />
      {reason !== null ? (
        <span className={styles.inline} data-approval-reject="true">
          <TextInput size={Size.Large} label={t('approvals.rejectReason')} value={reason} onChange={setReason} required />
          <Button size={Size.Small} color={Color.Navy} text={[t('approvals.reject')]} disabled={busy || reason.trim() === ''} actions={[() => void act('reject', { reason: reason.trim() }, t('approvals.rejected'))]} />
          <Button size={Size.Small} color={Color.Inactive} text={[t('modal.cancel')]} actions={[() => setReason(null)]} />
        </span>
      ) : null}
      {override && floorAcknowledgementPath ? (
        <FloorOverrideWindow
          floors={openFloors}
          acknowledgementPath={floorAcknowledgementPath}
          apiBaseUrl={apiBaseUrl}
          onApprove={async () => {
            setOverride(false);
            await act('approve', {}, t('approvals.approved'));
          }}
          onCancel={() => setOverride(false)}
          onError={onError}
        />
      ) : null}
    </>
  );
}
